/**
 * Admin sidebar menu — must stay aligned with `prisma/seed.ts` → `seedMenus()`
 * (`route` + Chinese `name`). `hasMenu(route)` checks these exact route strings.
 *
 * See docs/UI_I18N.md.
 */
import type { ReactNode } from 'react';
import {
  SettingOutlined,
  TeamOutlined,
  SwapOutlined,
  DashboardOutlined,
  UserOutlined,
  FileTextOutlined,
  WalletOutlined,
  HeartOutlined,
  CreditCardOutlined,
  GoldOutlined,
  GiftOutlined,
  ToolOutlined,
  FileSearchOutlined,
  BarChartOutlined,
  CommentOutlined,
  TransactionOutlined,
  ScissorOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import parityMenusJson from './php-parity-menus.generated.json';

/**
 * 左侧边栏**仅**使用 `groupParitySubmenus()`（旧 PHP 后台对照分组：系统 / 用户与会员 / EP / UO… / UOC）。
 * 不再提供「平台运营、EP 市场、UO OTC…」那套全站顶级分组。
 */
/** 与 `groupParitySubmenus()` 返回的分组 key 一致（含 UO 子分组） */
export const PHP_PARITY_SUBMENU_KEYS = [
  'platformDashboard',
  'paritySystem',
  'parityUser',
  'parityEp',
  'parityUoData',
  'parityUoFlow',
  'parityUoFund',
  'parityUoSplit',
  'parityUoPcc',
  'parityUoc',
] as const;

/** Pinned first tab after login (route must exist in seed after dashboard row is added). */
export const DEFAULT_HOME_TAB = {
  key: 'platform.dashboard',
  label: '数据看板',
  path: '/platform/dashboard',
  closable: false as const,
};

export interface AdminMenuLeaf {
  /** Same as `admin_menus.route` in DB */
  route: string;
  labelZh: string;
  path: string;
  icon?: ReactNode;
}

type ParityMenuJson = {
  items: Array<{
    route: string;
    labelZh: string;
    path: string;
    phpHref: string;
    nodeRoute?: string | null;
  }>;
};

const parityMenus = parityMenusJson as ParityMenuJson;

const PHP_PARITY_LEAVES: AdminMenuLeaf[] = parityMenus.items.map((i) => ({
  route: i.route,
  labelZh: i.labelZh,
  path: i.path,
  icon: <FileSearchOutlined />,
}));

function pickParityLeavesInOrder(
  allLeaves: AdminMenuLeaf[],
  routeOrder: readonly string[],
): AdminMenuLeaf[] {
  const m = new Map(allLeaves.map((l) => [l.route, l]));
  return routeOrder.map((r) => m.get(r)).filter((x): x is AdminMenuLeaf => x != null);
}

/** 原「UO 业务」下 parity.uo_* 再拆组（与 php-parity-menus 中顺序大致一致） */
const UO_DATA_ROUTES = [
  'parity.uo_uoconfig',
  'parity.uo_bonuslog',
  'parity.uo_uo',
  'parity.uo_fp',
  'parity.uo_uo_count',
  'parity.uo_usersearch',
] as const;
const UO_FLOW_ROUTES = [
  'parity.uo_userdonate',
  'parity.uo_userbalancelog',
  'parity.uo_opinion',
  'parity.uo_complaint',
  'parity.uo_configlog',
] as const;
const UO_FUND_ROUTES = [
  'parity.uo_activation',
  'parity.uo_userrechargeconfig',
  'parity.uo_userrecharge',
  'parity.uo_user',
] as const;
const UO_SPLIT_ROUTES = [
  'parity.uo_config',
  'parity.uo_uosplitprice',
  'parity.uo_unitprice',
  'parity.uo_uo_config_update',
  'parity.uo_pi',
  'parity.uo_splitlog',
] as const;
const UO_PCC_ROUTES = ['parity.uo_pcc', 'parity.uo_userpcc'] as const;

/** Node-only ops pages (no PHP parity row); visibility via `NODE_ONLY_ROUTE_NODES`. */
const NODE_UOC_OPS_LEAVES: AdminMenuLeaf[] = [
  {
    route: 'platform.uoc.transfer.queue',
    labelZh: 'UOC 转出排队',
    path: '/platform/uoc/transfer-queue',
    icon: <GoldOutlined />,
  },
];

/** 奖励运维 — 放在「系统管理」便于发现（勿只挂在 UOC 分组下）。 */
const NODE_REWARD_OPS_LEAVES: AdminMenuLeaf[] = [
  {
    route: 'platform.reward_distributions',
    labelZh: '奖励分发配置',
    path: '/platform/reward-distributions',
    icon: <GiftOutlined />,
  },
  {
    route: 'platform.static_reward.preview',
    labelZh: '静态奖励预览',
    path: '/platform/static-reward/preview',
    icon: <GiftOutlined />,
  },
  {
    route: 'platform.dynamic_rewards.ops',
    labelZh: '动态奖励运维',
    path: '/platform/dynamic-rewards/ops',
    icon: <BarChartOutlined />,
  },
  {
    route: 'platform.staging_reward_test',
    labelZh: 'Staging 动静态复测',
    path: '/platform/staging-reward-test',
    icon: <ExperimentOutlined />,
  },
];

/** ORB 数据统计 — Node 原生只读页，挂在「ORB · 数据与统计」。 */
const NODE_UO_DATA_LEAVES: AdminMenuLeaf[] = [
  {
    route: 'platform.sp_reinvest_wallet_orb',
    labelZh: '复投子账户 ORB',
    path: '/platform/sp-reinvest-wallet-orb',
    icon: <BarChartOutlined />,
  },
  {
    route: 'platform.sub_account.stats',
    labelZh: '子账户统计',
    path: '/platform/sub-account-stats',
    icon: <BarChartOutlined />,
  },
];

type NodeOnlySpec = string | string[];

/** platform.* routes without seed rows → permission node(s) or seeded menu route for sidebar RBAC */
const NODE_ONLY_ROUTE_NODES: Record<string, NodeOnlySpec> = {
  'platform.dashboard': ['platform.dashboard', 'platform.dashboard.view'],
  'platform.orb_top100': ['platform.dashboard', 'platform.dashboard.view'],
  'platform.orb_cap': ['platform.dashboard', 'platform.dashboard.view'],
  'platform.user.tree': 'platform.user.view',
  'platform.binary_tree': 'platform.binary_tree',
  'platform.user.freeze': 'platform.user.freeze',
  'platform.user.shift_upline': 'platform.user.shift_upline',
  'bot_flag.manage': 'bot_flag.list',
  'bot_flag.batch': ['bot_flag.batch_create', 'bot_flag.update'],
  'platform.uoc.transfer.queue': 'platform.withdraw.approve',
  'platform.static_reward.preview': ['platform.stats.view', 'platform.stats.run_settlement'],
  'platform.dynamic_rewards.ops': ['platform.stats.run_settlement', 'platform.stats.view'],
  'platform.staging_reward_test': ['platform.stats.run_settlement', 'platform.stats.view'],
  'platform.sp_reinvest_wallet_orb': 'platform.stats.view',
  'platform.sub_account.stats': 'platform.stats.view',
  'platform.reward_distributions': [
    'system.config.list',
    'system.config.edit',
    'system.config',
    'platform.stats.view',
  ],
};

/** 旧 PHP 后台对照菜单：按业务拆成多组（侧栏不再显示「PHP 菜单对照」单组标题） */
function groupParitySubmenus(): {
  key: (typeof PHP_PARITY_SUBMENU_KEYS)[number];
  label: string;
  icon: ReactNode;
  children: AdminMenuLeaf[];
}[] {
  const uocRoutes = new Set([
    'parity.uo_uocrecharge',
    'parity.uo_uoc',
    'parity.uo_userbalancelogz',
    'parity.uo_userbalancelogt',
  ]);
  const system = [
    ...PHP_PARITY_LEAVES.filter((l) => l.route.startsWith('parity.system_')),
    ...NODE_REWARD_OPS_LEAVES,
  ];
  const user = [
    ...PHP_PARITY_LEAVES.filter(
      (l) =>
        l.route.startsWith('parity.user_') &&
        l.route !== 'parity.user_transactionep' &&
        l.route !== 'parity.user_proportion',
    ),
    ...NODE_USER_NETWORK_LEAVES,
    ...NODE_USER_OPS_LEAVES,
    ...NODE_BOT_LEAVES,
  ];
  // EP 交易配置 → Node `ep_price_tier` page (not php_user_proportion mirror).
  // Runtime EP sell/pay reads ep_price_tier; parity mirror is migration-only.
  const epTx = PHP_PARITY_LEAVES.find((l) => l.route === 'parity.user_transactionep');
  const ep: AdminMenuLeaf[] = [
    ...(epTx ? [epTx] : []),
    {
      route: 'ep.pricing',
      labelZh: 'EP 交易配置',
      path: routeToPath('ep.pricing'),
      icon: <SwapOutlined />,
    },
  ];
  const uoAll = PHP_PARITY_LEAVES.filter((l) => l.route.startsWith('parity.uo_'));
  const uoc = [...uoAll.filter((l) => uocRoutes.has(l.route)), ...NODE_UOC_OPS_LEAVES];
  const uoNonUoc = uoAll.filter((l) => !uocRoutes.has(l.route));
  const uoData = [...pickParityLeavesInOrder(uoNonUoc, UO_DATA_ROUTES), ...NODE_UO_DATA_LEAVES];
  const uoFlow = pickParityLeavesInOrder(uoNonUoc, UO_FLOW_ROUTES);
  const uoFund = pickParityLeavesInOrder(uoNonUoc, UO_FUND_ROUTES);
  const uoSplit = pickParityLeavesInOrder(uoNonUoc, UO_SPLIT_ROUTES);
  const uoPcc = pickParityLeavesInOrder(uoNonUoc, UO_PCC_ROUTES);
  const dashboard: AdminMenuLeaf[] = [
    {
      route: 'platform.dashboard',
      labelZh: '数据看板',
      path: routeToPath('platform.dashboard'),
      icon: <DashboardOutlined />,
    },
    {
      route: 'platform.orb_top100',
      labelZh: 'ORB(主+子) Top100',
      path: '/platform/orb-top100',
      icon: <DashboardOutlined />,
    },
    {
      route: 'platform.orb_cap',
      labelZh: 'ORB 封顶模拟',
      path: '/platform/orb-cap',
      icon: <DashboardOutlined />,
    },
  ];
  const groups: {
    key: (typeof PHP_PARITY_SUBMENU_KEYS)[number];
    label: string;
    icon: ReactNode;
    children: AdminMenuLeaf[];
  }[] = [
    { key: 'platformDashboard', label: '仪表盘', icon: <DashboardOutlined />, children: dashboard },
    { key: 'paritySystem', label: '系统管理', icon: <SettingOutlined />, children: system },
    { key: 'parityUser', label: '用户与会员', icon: <UserOutlined />, children: user },
    { key: 'parityEp', label: 'EP 市场', icon: <SwapOutlined />, children: ep },
    { key: 'parityUoData', label: 'ORB · 数据与统计', icon: <BarChartOutlined />, children: uoData },
    { key: 'parityUoFlow', label: 'ORB · 流水与客诉', icon: <CommentOutlined />, children: uoFlow },
    { key: 'parityUoFund', label: 'ORB · 充值与转币', icon: <TransactionOutlined />, children: uoFund },
    { key: 'parityUoSplit', label: 'ORB · 拆分与币价', icon: <ScissorOutlined />, children: uoSplit },
    { key: 'parityUoPcc', label: 'ORB · PCC', icon: <CreditCardOutlined />, children: uoPcc },
    { key: 'parityUoc', label: 'UOC 与链上', icon: <GoldOutlined />, children: uoc },
  ];
  return groups.filter((g) => g.children.length > 0);
}

/** Map seed `route` → React Router path (leading slash). Keep in sync with seed menu order/names. */
export function routeToPath(route: string): string {
  const explicit: Record<string, string> = {
    'system.admin': '/system/admins',
    'platform.dashboard': '/platform/dashboard',
    'platform.orb_top100': '/platform/orb-top100',
    'platform.orb_cap': '/platform/orb-cap',
    'platform.user.list': '/platform/users',
    'platform.balance.log': '/platform/ledger',
    'platform.recharge.review': '/platform/recharge',
    'ep.orders': '/ep/orders',
    'otc.orders': '/otc/orders',
    'otc.trades': '/otc/trades',
    'donation.causes': '/donation/causes',
    'donation.log': '/donation/log',
    'pcc.cards': '/pcc/cards',
    'pcc.holdings': '/pcc/holdings',
    'platform.private_letter': '/platform/private-letter',
    'platform.opinion': '/platform/opinion',
    'system.upload_manage': '/system/upload-manage',
    'platform.ops_snapshot': '/platform/ops-snapshot',
    'platform.bonus_trade_log': '/platform/bonus-trade-log',
    'platform.pi_settlement': '/platform/pi-settlement',
    'platform.account_config_log': '/platform/account-config-log',
    'platform.uo_count_report': '/platform/uo-count-report',
    'platform.uoc_mint_log': '/platform/uoc-mint-log',
    'platform.uoc_withdraw_log': '/platform/uoc-withdraw-log',
    'system.php_mirror': '/system/php-mirror',
    'platform.reward_distributions': '/platform/reward-distributions',
    'platform.static_reward.preview': '/platform/static-reward/preview',
    'platform.dynamic_rewards.ops': '/platform/dynamic-rewards/ops',
    'platform.staging_reward_test': '/platform/staging-reward-test',
    'platform.sp_reinvest_wallet_orb': '/platform/sp-reinvest-wallet-orb',
    'platform.sub_account.stats': '/platform/sub-account-stats',
    'platform.uoc.transfer.queue': '/platform/uoc/transfer-queue',
  };
  if (explicit[route]) return explicit[route];
  return `/${route.replace(/\./g, '/')}`;
}

/** 冻结 / 迁移 — 独立运维页 */
const NODE_USER_OPS_LEAVES: AdminMenuLeaf[] = [
  {
    route: 'platform.user.freeze',
    labelZh: '冻结账户',
    path: '/platform/user/freeze',
    icon: <UserOutlined />,
  },
  {
    route: 'platform.user.shift_upline',
    labelZh: '迁移账户',
    path: '/platform/user/shift-upline',
    icon: <UserOutlined />,
  },
];

/** 虚拟账户 — bot_flag 管理页 */
const NODE_BOT_LEAVES: AdminMenuLeaf[] = [
  {
    route: 'bot_flag.manage',
    labelZh: '虚拟账户',
    path: routeToPath('bot_flag.manage'),
    icon: <TeamOutlined />,
  },
  {
    route: 'bot_flag.batch',
    labelZh: '虚拟用户批量创建',
    path: routeToPath('bot_flag.batch'),
    icon: <TeamOutlined />,
  },
];

/** 网体 / 推荐树 — Node 原生页（无 PHP parity 行），挂在「用户与会员」分组。 */
const NODE_USER_NETWORK_LEAVES: AdminMenuLeaf[] = [
  {
    route: 'platform.user.tree',
    labelZh: '推荐树',
    path: routeToPath('platform.user.tree'),
    icon: <UserOutlined />,
  },
  {
    route: 'platform.binary_tree',
    labelZh: '系谱图',
    path: routeToPath('platform.binary_tree'),
    icon: <UserOutlined />,
  },
];

/** Flat leaves: order = system block, then platform, then ep (matches seed). */
export const ADMIN_MENU_LEAVES: AdminMenuLeaf[] = [
  // system
  { route: 'system.admin', labelZh: '后台管理员', path: routeToPath('system.admin') },
  { route: 'system.role', labelZh: '角色管理', path: routeToPath('system.role') },
  { route: 'system.menu', labelZh: '菜单管理', path: routeToPath('system.menu') },
  { route: 'system.node', labelZh: '权限节点', path: routeToPath('system.node') },
  { route: 'system.config', labelZh: '系统配置', path: routeToPath('system.config') },
  { route: 'system.oplog', labelZh: '操作日志', path: routeToPath('system.oplog') },
  { route: 'system.backup', labelZh: '数据库备份', path: routeToPath('system.backup') },
  { route: 'system.cache', labelZh: '缓存清理', path: routeToPath('system.cache') },
  // platform
  {
    route: 'platform.dashboard',
    labelZh: '数据看板',
    path: routeToPath('platform.dashboard'),
    icon: <DashboardOutlined />,
  },
  {
    route: 'platform.orb_top100',
    labelZh: 'ORB(主+子) Top100',
    path: '/platform/orb-top100',
    icon: <DashboardOutlined />,
  },
  {
    route: 'platform.orb_cap',
    labelZh: 'ORB 封顶模拟',
    path: '/platform/orb-cap',
    icon: <DashboardOutlined />,
  },
  {
    route: 'platform.user.list',
    labelZh: '会员列表',
    path: routeToPath('platform.user.list'),
    icon: <UserOutlined />,
  },
  { route: 'platform.user.detail', labelZh: '会员详情', path: routeToPath('platform.user.detail') },
  { route: 'platform.user.adjust', labelZh: '余额调整', path: routeToPath('platform.user.adjust') },
  {
    route: 'platform.balance.log',
    labelZh: '余额日志',
    path: routeToPath('platform.balance.log'),
    icon: <FileTextOutlined />,
  },
  { route: 'platform.transfer_coin', labelZh: '给用户转币', path: routeToPath('platform.transfer_coin'), icon: <WalletOutlined /> },
  { route: 'platform.fp_stats', labelZh: 'FP捐赠统计', path: routeToPath('platform.fp_stats') },
  { route: 'platform.market_cap', labelZh: '市值统计', path: routeToPath('platform.market_cap') },
  { route: 'platform.deep_search', labelZh: '高级搜索', path: routeToPath('platform.deep_search'), icon: <UserOutlined /> },
  { route: 'platform.user.tree', labelZh: '推荐树', path: routeToPath('platform.user.tree') },
  { route: 'platform.binary_tree', labelZh: '系谱图', path: routeToPath('platform.binary_tree') },
  { route: 'platform.level', labelZh: '等级管理', path: routeToPath('platform.level') },
  { route: 'platform.level.upgrade', labelZh: '等级晋级', path: routeToPath('platform.level.upgrade') },
  {
    route: 'platform.recharge.review',
    labelZh: '充值审核',
    path: routeToPath('platform.recharge.review'),
    icon: <WalletOutlined />,
  },
  { route: 'platform.recharge.log', labelZh: '充值记录', path: routeToPath('platform.recharge.log') },
  { route: 'platform.withdraw.review', labelZh: '提现审核', path: routeToPath('platform.withdraw.review') },
  { route: 'platform.withdraw.log', labelZh: '提现记录', path: routeToPath('platform.withdraw.log') },
  { route: 'platform.transfer.log', labelZh: '转账记录', path: routeToPath('platform.transfer.log') },
  { route: 'platform.split.log', labelZh: '拆分', path: routeToPath('platform.split.log') },
  { route: 'platform.mentorship.log', labelZh: '辅导奖记录', path: '/platform/mentorship/log' },
  {
    route: 'platform.uoc.transfer.queue',
    labelZh: 'UOC 转出排队',
    path: '/platform/uoc/transfer-queue',
  },
  {
    route: 'platform.static_reward.preview',
    labelZh: '静态奖励预览',
    path: '/platform/static-reward/preview',
  },
  {
    route: 'platform.dynamic_rewards.ops',
    labelZh: '动态奖励运维',
    path: '/platform/dynamic-rewards/ops',
  },
  {
    route: 'platform.reward_distributions',
    labelZh: '奖励分发配置',
    path: '/platform/reward-distributions',
  },
  { route: 'platform.announcement', labelZh: '公告管理', path: routeToPath('platform.announcement') },
  { route: 'platform.banner', labelZh: 'Banner', path: routeToPath('platform.banner') },
  { route: 'platform.help', labelZh: '帮助中心', path: routeToPath('platform.help') },
  { route: 'platform.faq', labelZh: 'FAQ', path: routeToPath('platform.faq') },
  { route: 'platform.referral.bonus', labelZh: '推荐奖励', path: routeToPath('platform.referral.bonus') },
  { route: 'platform.level.bonus', labelZh: '等级奖励', path: routeToPath('platform.level.bonus') },
  { route: 'platform.node.bonus', labelZh: '节点奖励', path: routeToPath('platform.node.bonus') },
  { route: 'platform.fork.event', labelZh: '分红事件', path: routeToPath('platform.fork.event') },
  { route: 'platform.fork.log', labelZh: '分红记录', path: routeToPath('platform.fork.log') },
  { route: 'platform.token.stats', labelZh: '代币统计', path: routeToPath('platform.token.stats') },
  { route: 'platform.user.stats', labelZh: '用户统计', path: routeToPath('platform.user.stats') },
  {
    route: 'platform.sp_reinvest_wallet_orb',
    labelZh: '复投子账户 ORB',
    path: routeToPath('platform.sp_reinvest_wallet_orb'),
  },
  {
    route: 'platform.sub_account.stats',
    labelZh: '子账户统计',
    path: routeToPath('platform.sub_account.stats'),
  },
  { route: 'platform.trend.new', labelZh: '新增趋势', path: routeToPath('platform.trend.new') },
  { route: 'platform.trend.active', labelZh: '活跃趋势', path: routeToPath('platform.trend.active') },
  { route: 'platform.kyc', labelZh: 'KYC审核', path: routeToPath('platform.kyc') },
  { route: 'platform.ticket', labelZh: '投诉工单', path: routeToPath('platform.ticket') },
  { route: 'platform.blacklist', labelZh: '黑名单', path: routeToPath('platform.blacklist') },
  { route: 'platform.whitelist', labelZh: '白名单', path: routeToPath('platform.whitelist') },
  { route: 'platform.activity', labelZh: '活动管理', path: routeToPath('platform.activity') },
  { route: 'platform.task', labelZh: '任务管理', path: routeToPath('platform.task') },
  { route: 'platform.push', labelZh: '消息推送', path: routeToPath('platform.push') },
  // 与旧 PHP EasyAdmin 对照的补充入口（无 Node 接口的项为说明页）
  { route: 'platform.private_letter', labelZh: '私信管理', path: routeToPath('platform.private_letter') },
  { route: 'platform.opinion', labelZh: '意见建议', path: routeToPath('platform.opinion') },
  { route: 'platform.ops_snapshot', labelZh: '运营数据概况', path: routeToPath('platform.ops_snapshot') },
  { route: 'platform.bonus_trade_log', labelZh: '挂买挂卖记录', path: routeToPath('platform.bonus_trade_log') },
  { route: 'platform.pi_settlement', labelZh: '沉淀奖金转币', path: routeToPath('platform.pi_settlement') },
  { route: 'platform.account_config_log', labelZh: '账户沉淀记录', path: routeToPath('platform.account_config_log') },
  { route: 'platform.uo_count_report', labelZh: '指导销售统计', path: routeToPath('platform.uo_count_report') },
  { route: 'platform.uoc_mint_log', labelZh: 'UOC 铸造记录', path: routeToPath('platform.uoc_mint_log') },
  { route: 'platform.uoc_withdraw_log', labelZh: 'UOC 提币记录', path: routeToPath('platform.uoc_withdraw_log') },
  // ep
  {
    route: 'ep.orders',
    labelZh: 'EP 订单',
    path: routeToPath('ep.orders'),
    icon: <SwapOutlined />,
  },
  { route: 'ep.disputes', labelZh: 'EP 争议', path: routeToPath('ep.disputes'), icon: <SwapOutlined /> },
  { route: 'ep.pricing', labelZh: 'EP 价格档位', path: routeToPath('ep.pricing'), icon: <SwapOutlined /> },
  // otc
  {
    route: 'otc.orders',
    labelZh: 'ORB 挂单记录',
    path: routeToPath('otc.orders'),
    icon: <SwapOutlined />,
  },
  {
    route: 'otc.trades',
    labelZh: 'ORB 成交记录',
    path: routeToPath('otc.trades'),
    icon: <SwapOutlined />,
  },
  // donation
  {
    route: 'donation.causes',
    labelZh: '捐赠项目',
    path: routeToPath('donation.causes'),
    icon: <HeartOutlined />,
  },
  {
    route: 'donation.log',
    labelZh: '捐赠记录',
    path: routeToPath('donation.log'),
    icon: <HeartOutlined />,
  },
  // pcc
  {
    route: 'pcc.cards',
    labelZh: '推广卡',
    path: routeToPath('pcc.cards'),
    icon: <CreditCardOutlined />,
  },
  {
    route: 'pcc.holdings',
    labelZh: '推广卡持仓',
    path: routeToPath('pcc.holdings'),
    icon: <CreditCardOutlined />,
  },
  // uoc
  {
    route: 'uoc.config',
    labelZh: '源石币配置',
    path: routeToPath('uoc.config'),
    icon: <GoldOutlined />,
  },
  {
    route: 'uoc.recharge',
    labelZh: '源石币充值',
    path: routeToPath('uoc.recharge'),
    icon: <GoldOutlined />,
  },
  {
    route: 'uoc.log',
    labelZh: '源石币日志',
    path: routeToPath('uoc.log'),
    icon: <GoldOutlined />,
  },
  // activation
  {
    route: 'activation.pool',
    labelZh: '激活码池',
    path: routeToPath('activation.pool'),
    icon: <GiftOutlined />,
  },
  {
    route: 'activation.distribute',
    labelZh: '激活码分发',
    path: routeToPath('activation.distribute'),
    icon: <GiftOutlined />,
  },
  {
    route: 'activation.batch',
    labelZh: '激活码批次',
    path: routeToPath('activation.batch'),
    icon: <GiftOutlined />,
  },
  // tools（与 seed 中 finance/operator 路由对齐）
  {
    route: 'recharge_wallet.config',
    labelZh: 'TP 链上充值',
    path: routeToPath('recharge_wallet.config'),
    icon: <ToolOutlined />,
  },
  {
    route: 'sub_account.manage',
    labelZh: '子账户管理',
    path: routeToPath('sub_account.manage'),
    icon: <ToolOutlined />,
  },
  { route: 'bot_flag.manage', labelZh: '虚拟用户', path: routeToPath('bot_flag.manage'), icon: <TeamOutlined /> },
  { route: 'bot_flag.batch', labelZh: '虚拟用户批量创建', path: routeToPath('bot_flag.batch'), icon: <TeamOutlined /> },
  { route: 'upload.log', labelZh: '上传日志', path: routeToPath('upload.log'), icon: <ToolOutlined /> },
  { route: 'unit_price.config', labelZh: '单价管理', path: routeToPath('unit_price.config'), icon: <ToolOutlined /> },
  { route: 'user_op.snapshot', labelZh: '用户操作日志', path: routeToPath('user_op.snapshot'), icon: <ToolOutlined /> },
  { route: 'honor.journey', labelZh: '荣誉旅程', path: routeToPath('honor.journey'), icon: <ToolOutlined /> },
  { route: 'system.upload_manage', labelZh: '上传素材管理', path: routeToPath('system.upload_manage'), icon: <SettingOutlined /> },
  { route: 'system.php_mirror', labelZh: 'PHP 镜像表 (raw)', path: routeToPath('system.php_mirror'), icon: <SettingOutlined /> },
  ...PHP_PARITY_LEAVES,
];

/**
 * `php-parity-menus` nodeRoute strings sometimes use kebab segments while `routeToPath` uses
 * underscores — register both so RBAC can resolve the linked `admin_menus.route`.
 */
const NODE_PATH_ALIASES: [string, string][] = [
  ['/platform/transfer-coin', '/platform/transfer_coin'],
  ['/bot-flag/manage', '/bot_flag/manage'],
  ['/user-op-snapshot', '/user_op/snapshot'],
  ['/recharge-wallet/config', '/recharge_wallet/config'],
  ['/platform/fp-stats', '/platform/fp_stats'],
  ['/platform/market-cap', '/platform/market_cap'],
  ['/unit-price/config', '/unit_price/config'],
  ['/platform/deep-search', '/platform/deep_search'],
];

/** Normalized `path` → `admin_menus.route` (for parity `nodeRoute` → real menu RBAC). */
const PATH_TO_MENU_ROUTE: Map<string, string> = (() => {
  const m = new Map<string, string>();
  for (const l of ADMIN_MENU_LEAVES) {
    const p = l.path.replace(/\/$/, '');
    if (!m.has(p)) m.set(p, l.route);
  }
  for (const [a, b] of NODE_PATH_ALIASES) {
    const ra = m.get(a);
    const rb = m.get(b);
    if (ra && !m.has(b)) m.set(b, ra);
    if (rb && !m.has(a)) m.set(a, rb);
  }
  return m;
})();

/**
 * `parity.*` menu rows are not always granted; roles often only have `platform.*` / `ep.*`.
 * If the linked `nodeRoute` in `php-parity-menus` matches a seeded menu path, use that for visibility.
 */
export function canAccessAdminMenuLeaf(
  leaf: AdminMenuLeaf,
  hasMenu: (k: string) => boolean,
  hasNode: (k: string) => boolean,
): boolean {
  if (hasMenu(leaf.route) || hasNode(leaf.route)) return true;
  const nodeOnly = NODE_ONLY_ROUTE_NODES[leaf.route];
  if (nodeOnly) {
    const keys = Array.isArray(nodeOnly) ? nodeOnly : [nodeOnly];
    if (keys.some((k) => hasMenu(k) || hasNode(k))) return true;
  }
  if (!leaf.route.startsWith('parity.')) return false;
  const spec = parityMenus.items.find((i) => i.route === leaf.route);
  const nodePath = spec?.nodeRoute?.replace(/\/$/, '');
  if (!nodePath) return false;
  const linked = PATH_TO_MENU_ROUTE.get(nodePath);
  if (!linked) return false;
  return hasMenu(linked) || hasNode(linked);
}

export function groupMenuLeaves(): {
  key: (typeof PHP_PARITY_SUBMENU_KEYS)[number];
  label: string;
  icon: ReactNode;
  children: AdminMenuLeaf[];
}[] {
  return groupParitySubmenus();
}
